import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, skip, Subscription } from 'rxjs';
import { WorkflowPermissionList } from '../../workflow.model';
import { workflowPermissionList } from '../../workflow.constants';
import {
  WORKFLOW_PERMISSION_COLUMNS_SORT,
  WORKFLOW_PERMISSION_VIEW_DETAILS_ACTION,
  WORKFLOW_PERMISSION_FILTER_TABS,
} from './workflow-permission-list.constants';
import { WorkflowService } from '../../workflow.service';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { SearchFieldComponent } from '../../../../../../ui-components/lib/search-field/search-field.component';
import {
  initialSearchTerm,
  SearchService,
} from '../../../../../../ui-components/lib/search-field/search.service';
import { TableComponent } from '../../../../../../ui-components/lib/table/table.component';
import { UserDropdownViewComponent } from '../../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ActionList } from '../../../../../common/models/action-list.model';
import { Meta } from '../../../../../common/models/api-response.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { SearchEnum } from '../../../../../common/enums/search.enum';

@Component({
  selector: 'lib-workflow-permission-list',
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    BadgeComponent,
    TableComponent,
    AvatarComponent,
    PaginatorComponent,
    UserDropdownViewComponent,
    ActionListComponent,
    SearchFieldComponent,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './workflow-permission-list.component.html',
  styleUrl: './workflow-permission-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowPermissionListComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  workflowService = inject(WorkflowService);
  transloco = inject(TranslocoService);
  router = inject(Router);
  tenantService = inject(TenantService);
  featureFlagsService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the panel header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );
  private readonly searchService = inject(SearchService);

  defaultSort = signal<{ key: string; value: 'asc' | 'desc' }>({
    key: '',
    value: 'asc',
  });
  activeSort = signal<{ key: string; value: 'asc' | 'desc' }>(
    this.defaultSort()
  );

  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);
  isLoading = signal<boolean>(false);
  isOwnerLoading = signal<boolean>(false);
  currentOwnerPage = signal<number>(1);
  totalOwnerPages = signal<number>(1);
  totalOwnerCount = signal<number>(0);
  ownerDropdownList = signal<any[]>([]);
  totalWorkflowsCount = signal<string>('');
  permissionList = signal<WorkflowPermissionList[]>(workflowPermissionList);

  viewDetailsAction = signal<ActionList>({
    ...WORKFLOW_PERMISSION_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(
      WORKFLOW_PERMISSION_VIEW_DETAILS_ACTION.label
    ),
  });
  columns = signal<TableColumns[]>(
    WORKFLOW_PERMISSION_COLUMNS_SORT.map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    WORKFLOW_PERMISSION_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal(initialSearchTerm());
  isMoreThanThreeCharacters = signal(
    this.searchService.isMinLength(this.searchTerm())
  );
  private readonly searchTerm$ = toObservable(this.searchTerm);
  subscriptions = new Subscription();

  ngOnInit() {
    this.getWorkflowList();
    this.searchTermWrapper();
  }

  searchTermWrapper() {
    this.subscriptions.add(
      this.searchTerm$
        .pipe(skip(1), debounceTime(300), distinctUntilChanged())
        .subscribe(term => {
          this.handleSearchTerm(term);
        })
    );
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      this.getWorkflowList();
    }
  }

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set(this.defaultSort());
    }
    this.currentPage.set(1);
    this.getWorkflowList();
  }

  getWorkflowList() {
    this.isLoading.set(true);
    this.workflowService
      .getWorkflowPermissionList(
        this.currentPage(),
        this.currentSize(),
        this.activeSort(),
        this.searchService.checkSearchLength(this.searchTerm())
      )
      .subscribe({
        next: (res: any) => {
          this.handleGetWorkflowListSuccess(res);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  handleGetWorkflowListSuccess(res: {
    data: WorkflowPermissionList[];
    meta: Meta;
  }) {
    this.permissionList.set(res.data);
    this.totalPages.set(res.meta.last_page);
    this.totalWorkflowsCount.set(res.meta.total.toString());
  }

  getWorkflowOwnerDropdown(event: any, id: string | number) {
    this.currentOwnerPage.set(event.currentPage);
    this.isOwnerLoading.set(true);
    if (this.currentOwnerPage() === 1) {
      this.ownerDropdownList.set([]);
      this.totalOwnerCount.set(0);
    }
    this.workflowService
      .getWorkflowOwnerDropdown(id, event.search, this.currentOwnerPage())
      .subscribe(response => this.handleOwnerDropdownSuccess(response));
  }

  handleOwnerDropdownSuccess(response: { data: any[]; meta: any }) {
    this.currentOwnerPage.set(response.meta.current_page);
    this.totalOwnerPages.set(response.meta.last_page);
    this.totalOwnerCount.set(response.meta.total);
    this.setOwnerDropdownList(response.data);
    this.isOwnerLoading.set(false);
  }

  setOwnerDropdownList(data: any[]) {
    if (this.currentOwnerPage() === 1) {
      this.ownerDropdownList.set(data);
    } else {
      this.ownerDropdownList().push(...data);
    }
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getWorkflowList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getWorkflowList();
  }

  viewWorkflowDetails(workflow: any) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.DETAILS}/${workflow.id}/view`
      )
    );
  }

  getViewActionList(workflowId: number): ActionList[] {
    this.viewDetailsAction().routerLinks = [
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.DETAILS}/${workflowId}/view`
      ),
    ];
    return [this.viewDetailsAction()];
  }

  actionListItemClick(action: string, row: WorkflowPermissionList) {
    if (action === 'view') {
      this.router.navigateByUrl(
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.DETAILS}/${row.id}/view`
        )
      );
    }
  }
}
